/**
 * GPX -> Wahoo-friendly TCX
 *
 * Workaround tested on Wahoo ELEMNT ROAM:
 *
 * 1. Read GPX track.
 * 2. Convert GPX <wpt> waypoints into TCX CoursePoints.
 * 3. Snap every waypoint to the nearest REAL trackpoint.
 * 4. Sort CoursePoints in track order.
 * 5. Generate unique monotonically increasing Trackpoint timestamps.
 * 6. CoursePoint time EXACTLY matches its corresponding Trackpoint time.
 * 7. Preserve POIs as Generic.
 * 8. Add two Right CoursePoints ("keeper cues"), because Wahoo appears
 *    to discard a cue sheet containing only Generic/Straight points.
 */

const TCX_NAMESPACE = "http://www.garmin.com/xmlschemas/TrainingCenterDatabase/v2";

const SCHEMA_LOCATION = `${TCX_NAMESPACE} http://www8.garmin.com/xmlschemas/TrainingCenterDatabasev2.xsd`;

// Wahoo "keeper" cues: Generic CoursePoints work, but ROAM appears capable of
// rejecting/regenerating the whole cuesheet when there are no directional
// CoursePoints. Two Right points make the Generic POIs appear.
const KEEPER_FRACTIONS = [0.07, 0.13];

const BASE_TIME_MS = Date.UTC(2026, 0, 1, 0, 0, 0);


// ---------------------------------------------------------------------
// XML helpers
// ---------------------------------------------------------------------

const localName = (node) => node.localName || node.nodeName.split(":").pop();

const directChildren = (parent, name) => {
  const result = [];
  for (let i = 0; i < parent.childNodes.length; i++) {
    const node = parent.childNodes[i];
    if (node.nodeType === 1 && localName(node) === name) {
      result.push(node);
    }
  }
  return result;
};

const firstDirectChild = (parent, name) => directChildren(parent, name)[0] || null;

const allElements = (parent, name) => {
  const result = [];
  const nodes = parent.getElementsByTagName("*");
  for (let i = 0; i < nodes.length; i++) {
    if (localName(nodes[i]) === name) {
      result.push(nodes[i]);
    }
  }
  return result;
};

const textOf = (element, fallback = "") => {
  if (!element || !element.textContent) {
    return fallback;
  }
  return element.textContent.trim();
};

const escapeXml = (value) => String(value)
  .replace(/&/g, "&amp;")
  .replace(/</g, "&lt;")
  .replace(/>/g, "&gt;")
  .replace(/"/g, "&quot;")
  .replace(/'/g, "&apos;");


// ---------------------------------------------------------------------
// Geometry
// ---------------------------------------------------------------------

const haversine = (a, b) => {
  const R = 6371000;
  const toRadians = (degrees) => degrees * Math.PI / 180;

  const lat1 = toRadians(a.lat);
  const lat2 = toRadians(b.lat);
  const dLat = toRadians(b.lat - a.lat);
  const dLon = toRadians(b.lon - a.lon);

  const h = Math.pow(Math.sin(dLat / 2), 2) +
    Math.cos(lat1) * Math.cos(lat2) * Math.pow(Math.sin(dLon / 2), 2);

  return 2 * R * Math.asin(Math.sqrt(h));
};


// ---------------------------------------------------------------------
// Time
// ---------------------------------------------------------------------

const isoTime = (seconds) => new Date(BASE_TIME_MS + seconds * 1000)
  .toISOString()
  .replace(".000Z", "Z");


// ---------------------------------------------------------------------
// Read GPX
// ---------------------------------------------------------------------

// `code` lets the UI show a translated message, `message` is the fallback
const gpxError = (code, message) => {
  const error = new Error(message);
  error.code = code;
  return error;
};

export const parseGpx = (xml) => {
  const document = new DOMParser().parseFromString(xml, "application/xml");

  const trackElement = allElements(document, "trk")[0];
  if (!trackElement) {
    throw gpxError("no_trk", "No <trk> found in GPX");
  }

  const courseName = textOf(firstDirectChild(trackElement, "name"), "Route");

  const segmentElements = directChildren(trackElement, "trkseg");
  if (!segmentElements.length) {
    throw gpxError("no_trkseg", "No <trkseg> found in GPX");
  }

  const trackpointElements = segmentElements.reduce(
    (points, segment) => points.concat(directChildren(segment, "trkpt")),
    []
  );
  if (trackpointElements.length < 2) {
    throw gpxError("few_trkpt", "Track must contain at least 2 <trkpt> elements");
  }

  const track = trackpointElements.map((element) => ({
    lat: Number(element.getAttribute("lat")),
    lon: Number(element.getAttribute("lon")),
  }));

  const waypoints = directChildren(document.documentElement, "wpt").map((element) => ({
    name: textOf(firstDirectChild(element, "name"), "POI"),
    lat: Number(element.getAttribute("lat")),
    lon: Number(element.getAttribute("lon")),
  }));

  return {courseName, track, waypoints};
};


// ---------------------------------------------------------------------
// Cumulative track distance
// ---------------------------------------------------------------------

const getCumulativeDistance = (track) => {
  const cumulativeDistance = [0];
  for (let i = 1; i < track.length; i++) {
    cumulativeDistance.push(cumulativeDistance[i - 1] + haversine(track[i - 1], track[i]));
  }
  return cumulativeDistance;
};


// ---------------------------------------------------------------------
// Snap every waypoint to nearest REAL trackpoint
//
// This is important for Wahoo.
// We don't merely put the CoursePoint "near" the route.
// Its coordinates become IDENTICAL to an existing Trackpoint.
// ---------------------------------------------------------------------

const getCoursePoints = (waypoints, track) => {
  const coursePoints = waypoints.map((waypoint) => {
    let bestIndex = 0;
    let bestDistance = Infinity;

    for (let i = 0; i < track.length; i++) {
      const distance = haversine(waypoint, track[i]);
      if (distance < bestDistance) {
        bestDistance = distance;
        bestIndex = i;
      }
    }

    return {
      index: bestIndex,
      name: waypoint.name,
      // IMPORTANT: use exact Trackpoint coordinates
      lat: track[bestIndex].lat,
      lon: track[bestIndex].lon,
      originalDistanceMeters: bestDistance,
      type: "Generic",
    };
  });

  // Keeper cues, placed on actual trackpoints
  KEEPER_FRACTIONS.forEach((fraction, number) => {
    const index = Math.max(1, Math.min(
      track.length - 2,
      Math.round((track.length - 1) * fraction)
    ));

    coursePoints.push({
      index,
      name: `Right ${number + 1}`,
      lat: track[index].lat,
      lon: track[index].lon,
      originalDistanceMeters: 0,
      type: "Right",
    });
  });

  // CoursePoints MUST follow track order
  coursePoints.sort((a, b) => a.index - b.index);

  return coursePoints;
};


// ---------------------------------------------------------------------
// Generate TCX
//
// Timestamps are generated by us: Trackpoint #N gets BASE_TIME + N seconds,
// so they are monotonic and a CoursePoint snapped to Trackpoint #N gets
// EXACTLY the same timestamp as that Trackpoint.
// ---------------------------------------------------------------------

export const buildTcx = ({courseName, track, waypoints}) => {
  const cumulativeDistance = getCumulativeDistance(track);
  const coursePoints = getCoursePoints(waypoints, track);
  const totalDistance = cumulativeDistance[cumulativeDistance.length - 1];
  const firstPoint = track[0];
  const lastPoint = track[track.length - 1];

  const output = [];

  output.push('<?xml version="1.0" encoding="UTF-8"?>');
  output.push(
    `<TrainingCenterDatabase xmlns="${TCX_NAMESPACE}" ` +
    `xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" ` +
    `xsi:schemaLocation="${SCHEMA_LOCATION}">`
  );
  output.push("  <Courses>");
  output.push("    <Course>");
  output.push(`      <Name>${escapeXml(courseName)}</Name>`);

  // Lap
  output.push("      <Lap>");
  output.push(`        <TotalTimeSeconds>${Math.max(1, track.length - 1)}</TotalTimeSeconds>`);
  output.push(`        <DistanceMeters>${totalDistance.toFixed(3)}</DistanceMeters>`);
  output.push("        <BeginPosition>");
  output.push(`          <LatitudeDegrees>${firstPoint.lat.toFixed(8)}</LatitudeDegrees>`);
  output.push(`          <LongitudeDegrees>${firstPoint.lon.toFixed(8)}</LongitudeDegrees>`);
  output.push("        </BeginPosition>");
  output.push("        <EndPosition>");
  output.push(`          <LatitudeDegrees>${lastPoint.lat.toFixed(8)}</LatitudeDegrees>`);
  output.push(`          <LongitudeDegrees>${lastPoint.lon.toFixed(8)}</LongitudeDegrees>`);
  output.push("        </EndPosition>");
  output.push("        <Intensity>Active</Intensity>");
  output.push("      </Lap>");

  // Track
  output.push("      <Track>");
  track.forEach((point, index) => {
    output.push("        <Trackpoint>");
    output.push(`          <Time>${isoTime(index)}</Time>`);
    output.push("          <Position>");
    output.push(`            <LatitudeDegrees>${point.lat.toFixed(8)}</LatitudeDegrees>`);
    output.push(`            <LongitudeDegrees>${point.lon.toFixed(8)}</LongitudeDegrees>`);
    output.push("          </Position>");
    output.push(`          <DistanceMeters>${cumulativeDistance[index].toFixed(3)}</DistanceMeters>`);
    output.push("        </Trackpoint>");
  });
  output.push("      </Track>");

  // CoursePoints
  coursePoints.forEach((point) => {
    output.push("      <CoursePoint>");
    output.push(`        <Name>${escapeXml(point.name)}</Name>`);
    // EXACT SAME timestamp as target Trackpoint
    output.push(`        <Time>${isoTime(point.index)}</Time>`);
    output.push("        <Position>");
    output.push(`          <LatitudeDegrees>${point.lat.toFixed(8)}</LatitudeDegrees>`);
    output.push(`          <LongitudeDegrees>${point.lon.toFixed(8)}</LongitudeDegrees>`);
    output.push("        </Position>");
    output.push(`        <PointType>${point.type}</PointType>`);
    output.push(`        <Notes>${escapeXml(point.name)}</Notes>`);
    output.push("      </CoursePoint>");
  });

  output.push("    </Course>");
  output.push("  </Courses>");
  output.push("</TrainingCenterDatabase>");

  return {
    xml: output.join("\n") + "\n",
    coursePoints,
    totalDistance,
  };
};
